import type { ActionTree } from 'vuex'
import dayjs from 'dayjs'
import type { CustomFieldState } from './types'
import type { RootState } from '@/store/modules/types'
import { magicDownload } from '@/utils/html-element/'
import {
  convertCurrencyFilterToMinorUnits,
  mergeSearchStringToFanFilters,
} from '@/utils/filter/'
import { convertToSanitizedCamelCase } from '@/utils/helpers/'
import { getContext } from '@/utils/general'

export const customFieldActions: ActionTree<CustomFieldState, RootState> = {
  async FETCH_CUSTOM_FIELDS(
    { state, rootState, commit },
    { type, searchFilter, filter },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOM_FIELDS', true)
      const data = await $api.customFields.getCustomFields(
        promoterOid,
        type,
        searchFilter,
        filter,
      )
      commit('SET_CUSTOM_FIELDS', data)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch custom fields',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOM_FIELDS', false)
    }
  },
  async CREATE_CUSTOM_FIELD(
    { rootState, commit },
    params,
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_CREATING_CUSTOM_FIELD', true)
      const data = await $api.customFields.createCustomField({
        promoterOid,
        ...params,
      })
      commit('ADD_TO_CUSTOM_FIELDS', data)
      return data
    }
    catch (error: any) {
      console.error(error)
      const rawMessage = error?.data?.message?.summary || ''
      const isNameConflict = error?.status === 409
        || /record exists|already exists|duplicate/i.test(rawMessage)
      const errorMessage = isNameConflict
        ? 'A field with this name already exists'
        : rawMessage || 'Failed to create custom field'
      $arNotification.push({
        type: 'error',
        message: errorMessage,
      })
      return {
        type: 'error',
        ...error.data,
      }
    }
    finally {
      commit('SET_IS_CREATING_CUSTOM_FIELD', false)
    }
  },
  async UPDATE_CUSTOM_FIELD({ state, rootState, commit }, params) {
    if (!rootState.authModule.account) {
      return null
    }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      const { data, status } = await $api.customFields.updateCustomField({
        promoterOid,
        ...params,
      })

      commit('PATCH_IN_CUSTOM_FIELDS', { oid: params.oid, changes: params })

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update custom field',
      })
      return false
    }
  },
  async DELETE_CUSTOM_FIELD({ state, rootState, commit }, oid: number) {
    if (!rootState.authModule.account) {
      return null
    }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      const { data, status } = await $api.customFields.deleteCustomField(
        promoterOid,
        oid,
      )
      commit('REMOVE_FROM_CUSTOM_FIELDS', oid)
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to delete custom field',
      })
      return false
    }
  },
  async EXPORT_CUSTOM_FIELD_CSV(
    { state, rootState, commit },
    { oid, fieldType, name },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      const audienceFilterJson = {
        logic: ['and'],
        conditions: [
          {
            name: 'custom-field',
            type: 'custom-field',
            data: {
              'condition': 'is_known',
              'custom-field-type': fieldType,
              'custom-field-oid': oid,
            },
          },
        ],
      }

      let type = ''

      switch (fieldType) {
        case 'dropdown-single':
        case 'dropdown-multi':
          type = 'customFieldOptions'
          break
        case 'date':
          type = 'customFieldDatetime'
          break
        case 'number':
          type = 'customFieldInteger'
          break
        case 'text':
        default:
          type = 'customFieldString'
      }

      const filterForApi
        = convertCurrencyFilterToMinorUnits(audienceFilterJson)
      const audienceFilter = JSON.stringify(
        mergeSearchStringToFanFilters(filterForApi, ''),
      )
      const customFieldProperties = {
        name: convertToSanitizedCamelCase(name),
        type: type,
      }
      const { data, status } = await $api.customFields.exportCustomFieldCsv(
        promoterOid,
        oid,
        customFieldProperties,
        audienceFilter,
      )

      if (status === 200) {
        const fileName = `attendees-${dayjs().format('DD/MM/YYYY')}.csv`
        magicDownload(data, fileName)
      }
      else {
        $arNotification.push({
          type: 'success',
          message: data,
          timeout: 5000,
        })
      }

      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to delete custom field',
      })
      return false
    }
  },
}
